import React, { useState } from 'react'
import styled from 'styled-components'
import { Text, Flex, MoleSelected, Skeleton } from 'vvs-uikit'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import useTheme from 'hooks/useTheme'
import { LegendaryColor, EpicColor, RareColor, SpecialColor, CommonColor } from 'views/Farms/styles'
import { NftDetails } from 'views/VvsMinerMoleMyMoles/types'

const NftContainer = styled(Flex)<{ selectable: boolean; disabled: boolean }>`
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;

  ${({ disabled }) => disabled && `opacity: 0.5;`}
  ${({ selectable, disabled }) =>
    selectable &&
    `
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
  `}
`
const NftTypeText = styled(Text)<{ rarity: NftDetails['rarity'] }>`
  font-family: 'Soleil';
  font-weight: 700;
  font-size: 13px;
  line-height: 18px;

  ${(props) => props.rarity === 'legendary' && LegendaryColor}
  ${(props) => props.rarity === 'epic' && EpicColor}
  ${(props) => props.rarity === 'rare' && RareColor}
  ${(props) => props.rarity === 'special' && SpecialColor}
  ${(props) => props.rarity === 'common' && CommonColor}
`

const StyledSkeleton = styled(Skeleton)`
  position: absolute;
  width: 100%;
  height: 100%;
  min-height: 140px;
`

const NftBorderBox = styled.div<{ selected: boolean; nftBorderColor?: string }>`
  position: absolute;
  inset: 0;
  border: 4px solid
    ${({ theme, nftBorderColor, selected }) =>
      selected ? theme.colors.primary : nftBorderColor || theme.colors.backgroundAlt};
`

const NftSelectedIcon = styled(MoleSelected)`
  position: absolute;
  bottom: 0;
  right: 0;
`

export const Nft: React.FC<{
  imgUrl: string
  name: string
  nftBorderColor: string
  rarity?: NftDetails['rarity']
  selectable?: boolean
  selected?: boolean
  disabled?: boolean
  onClick?: () => void
  hideRarity?: boolean
}> = ({
  imgUrl,
  name,
  nftBorderColor,
  rarity = 'common',
  selectable = true,
  selected = false,
  disabled = false,
  onClick,
  hideRarity = false,
}) => {
  const [isCompleted, setIsCompleted] = useState(false)
  const { theme } = useTheme()
  return (
    <NftContainer
      selectable={selectable}
      disabled={disabled}
      onClick={selectable && onClick ? onClick : () => undefined}
    >
      <Flex position="relative" width="100%">
        <LazyLoadImage
          src={imgUrl}
          effect="opacity"
          threshold={50}
          alt="mole"
          width="100%"
          afterLoad={() => setIsCompleted((prevState) => !prevState)}
          height={isCompleted ? 'auto' : '140px'}
          placeholder={<Skeleton height="140px" />}
        />
        {!isCompleted && <StyledSkeleton />}
        <NftBorderBox selected={selected} nftBorderColor={nftBorderColor}>
          {selected && <NftSelectedIcon />}
        </NftBorderBox>
      </Flex>

      <Flex flexDirection="column" gap="2px" alignItems="flex-start">
        <Text fontWeight="600" fontSize="16px" lineHeight="24px" color={theme.colors.text}>
          {name}
        </Text>
        {!hideRarity && <NftTypeText rarity={rarity}>{rarity.replace(/\b\w/, (c) => c.toUpperCase())}</NftTypeText>}
      </Flex>
    </NftContainer>
  )
}
